import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  #hasLoadedVideo = false;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    const loadVideo = () => {
      if (this.#hasLoadedVideo) return;

      for (const source of videoSources) {
        const { videoSource } = source.dataset;

        if (videoSource) source.setAttribute('src', videoSource);
      }

      videoElement.load();
      videoElement.play().catch(() => {});
      this.#hasLoadedVideo = true;
      this.#observer?.disconnect();
    };

    const IntersectionObserverCtor = globalThis.IntersectionObserver;
    if (typeof IntersectionObserverCtor === 'function') {
      this.#observer = new IntersectionObserverCtor(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            loadVideo();
          }
        },
        { threshold: 0.1 }
      );
      this.#observer.observe(this);
      return;
    }

    if (document.readyState === 'complete') {
      setTimeout(loadVideo, 1);
    } else {
      globalThis.addEventListener('load', loadVideo, { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
